import type { UserRole } from '@/lib/constants/roles';
import type { WidgetId } from '../config';
import {
  ActiveCampaignsKpi,
  CallsTodayKpi,
  EndUsersKpi,
  PendingPromptsKpi,
  ResellersKpi,
  TtsCreditsKpi,
  VoiceCreditsKpi,
} from './KpiWidgets';
import {
  CampaignAnalysisWidget,
  CampaignPillsWidget,
  ExpenditureChart,
  QuickActionsWidget,
  WeeklyCampaignsChart,
} from './OverviewWidgets';
import {
  RecentPromptsWidget,
  ResellerRunningWidget,
  RunningCampaignsWidget,
  UserDrilldownWidget,
} from './LiveWidgets';
import {
  CallDetailsWidget,
  DialerUsageWidget,
  PendingPromptsWidget,
  PilotCliWidget,
  UserwiseSummaryWidget,
} from './OperationsWidgets';
import {
  AgentGroupsCountWidget,
  InboundCampaignCountWidget,
  IvrCountWidget,
  TtsFlowCountWidget,
} from './ModuleWidgets';
import {
  AnswerRateKpi,
  CallOutcomeDonutWidget,
  CampaignTypeDonutWidget,
  ConnectedCallsKpi,
  DailyCallTrafficWidget,
  DialedCallsKpi,
  DndNumbersKpi,
  DndRateKpi,
  FailedCallsKpi,
  PendingCallsKpi,
  RecentCampaignsWidget,
  RetryCountKpi,
  RetryRateKpi,
  TopCampaignsByDialedWidget,
  TotalCampaignsKpi,
  TotalCallsDialedKpi,
  TotalNumbersKpi,
  TotalReadyKpi,
  TotalUsersKpi,
} from './DesignWidgets';

/** Render a widget body by id. */
export function renderWidget(id: WidgetId, role: UserRole): React.ReactNode {
  switch (id) {
    /* KPI */
    case 'kpi.voice-credits':
      return <VoiceCreditsKpi />;
    case 'kpi.tts-credits':
      return <TtsCreditsKpi />;
    case 'kpi.calls-today':
      return <CallsTodayKpi />;
    case 'kpi.active-campaigns':
      return <ActiveCampaignsKpi />;
    case 'kpi.resellers':
      return <ResellersKpi />;
    case 'kpi.end-users':
      return <EndUsersKpi />;
    case 'kpi.pending-prompts':
      return <PendingPromptsKpi />;

    /* New dashboard-design KPI tiles */
    case 'kpi.total-campaigns':
      return <TotalCampaignsKpi />;
    case 'kpi.dialed':
      return <DialedCallsKpi />;
    case 'kpi.connected':
      return <ConnectedCallsKpi />;
    case 'kpi.pending':
      return <PendingCallsKpi />;
    case 'kpi.dnd':
      return <DndNumbersKpi />;
    case 'kpi.retry-count':
      return <RetryCountKpi />;
    case 'kpi.answer-rate':
      return <AnswerRateKpi />;
    case 'kpi.dnd-rate':
      return <DndRateKpi />;
    case 'kpi.retry-rate':
      return <RetryRateKpi />;
    case 'kpi.failed-calls':
      return <FailedCallsKpi />;
    case 'kpi.total-users':
      return <TotalUsersKpi />;
    case 'kpi.total-numbers':
      return <TotalNumbersKpi />;
    case 'kpi.total-calls-dialed':
      return <TotalCallsDialedKpi />;
    case 'kpi.total-ready':
      return <TotalReadyKpi />;

    /* Overview */
    case 'overview.daily-traffic':
      return <DailyCallTrafficWidget />;
    case 'overview.outcome-donut':
      return <CallOutcomeDonutWidget />;
    case 'overview.type-donut':
      return <CampaignTypeDonutWidget />;
    case 'overview.top-dialed':
      return <TopCampaignsByDialedWidget />;
    case 'overview.recent-campaigns':
      return <RecentCampaignsWidget />;

    /* Overview */
    case 'overview.expenditure':
      return <ExpenditureChart />;
    case 'overview.weekly-campaigns':
      return <WeeklyCampaignsChart />;
    case 'overview.campaign-analysis':
      return <CampaignAnalysisWidget role={role} />;
    case 'overview.quick-actions':
      return <QuickActionsWidget role={role} />;

    /* Voice Campaigns */
    case 'voice.campaign-pills':
      return <CampaignPillsWidget />;
    case 'voice.running-campaigns':
      return <RunningCampaignsWidget />;
    case 'voice.reseller-running':
      return <ResellerRunningWidget />;
    case 'voice.user-drilldown':
      return <UserDrilldownWidget />;

    /* Inbound */
    case 'inbound.count':
      return <InboundCampaignCountWidget />;
    case 'inbound.agent-groups':
      return <AgentGroupsCountWidget />;

    /* Flows (IVR + TTS Flow) */
    case 'flows.ivr-count':
      return <IvrCountWidget />;
    case 'flows.tts-flow-count':
      return <TtsFlowCountWidget />;

    /* Voice Library */
    case 'library.recent-prompts':
      return <RecentPromptsWidget />;
    case 'library.pending-prompts':
      return <PendingPromptsWidget />;

    /* Operations */
    case 'ops.userwise-summary':
      return <UserwiseSummaryWidget />;
    case 'ops.call-details':
      return <CallDetailsWidget />;
    case 'ops.pilot-cli':
      return <PilotCliWidget />;
    case 'ops.dialer-usage':
      return <DialerUsageWidget />;

    default:
      return (
        <p className="py-4 text-center text-xs text-muted-foreground">
          Unknown widget.
        </p>
      );
  }
}
